/**
 * Single "moments" model for any photo or video captured during a turnover.
 *
 * A "moment" answers the question: when in the cleaner's day did this media
 * come from, and what is it allowed to be evidence of?
 *
 * - `arrival_condition`: before the cleaner touches anything. Evidence of how
 *   the guest left the unit, key locations, and any pre-existing damage.
 *   Sources: EntryWalkthroughStep, proof_media_by_standard_id at entry,
 *   condition_photos_by_room, primary/backup key proof.
 *
 * - `cleaning_proof`: captured during or right after cleaning. Evidence the
 *   work was done. Sources: task_submission_photos (before/after),
 *   standard_proof_uploads, room cover/issues, maintenance task photos.
 *
 * - `post_clean_staging`: captured after cleaning, before the next guest.
 *   Evidence of staged amenities and supply counts. Sources: guest_setup_state
 *   proof URLs, inventory item update photos, closeout keys photo.
 *
 * Today the database does not store a single discriminator column for every
 * URL; each surface stores moment context in different tables/columns. This
 * helper centralizes the mapping so the UI can label a URL consistently and
 * so we can later persist the discriminator without a UX rewrite.
 */

export type PhotoMoment =
  | "arrival_condition"
  | "cleaning_proof"
  | "post_clean_staging";

export interface PhotoMomentMeta {
  label: string;
  shortLabel: string;
  /** One-line description shown beside the badge or in tooltip. */
  description: string;
  /** Tailwind class fragment for badge background + text. */
  badgeClass: string;
}

export const PHOTO_MOMENT_META: Record<PhotoMoment, PhotoMomentMeta> = {
  arrival_condition: {
    label: "Arrival condition",
    shortLabel: "Arrival",
    description:
      "Captured at the door before cleaning — evidence of how the guest left the unit.",
    badgeClass:
      "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/30 dark:text-amber-200",
  },
  cleaning_proof: {
    label: "Cleaning proof",
    shortLabel: "Cleaning",
    description:
      "Captured during or right after cleaning — evidence the work was completed.",
    badgeClass:
      "bg-teal-50 text-teal-800 border-teal-300 dark:bg-teal-950/30 dark:text-teal-200",
  },
  post_clean_staging: {
    label: "Post-clean staging",
    shortLabel: "Staging",
    description:
      "Captured after cleaning — staged amenities, inventory counts, or keys returned.",
    badgeClass:
      "bg-indigo-50 text-indigo-800 border-indigo-300 dark:bg-indigo-950/30 dark:text-indigo-200",
  },
};

/**
 * Infer the moment from a storage path / public URL by looking at the
 * known prefixes used by each uploader in this codebase. Used as a fallback
 * when the caller did not pass an explicit `momentHint`.
 *
 * Returns `null` when nothing in the URL hints at a moment (caller should
 * decide a default).
 */
export function inferMomentFromUrl(url: string | null | undefined): PhotoMoment | null {
  if (!url) return null;
  const u = url.toLowerCase();

  if (u.includes("/entry_walkthrough/")) return "arrival_condition";
  if (u.includes("/cleaning_submissions/")) return "cleaning_proof";
  if (u.includes("/guest_setup/")) return "post_clean_staging";
  if (u.includes("/closeout_keys/")) return "post_clean_staging";
  if (u.includes("/inventory_check_photos/")) return "post_clean_staging";
  if (u.includes("/inventory-scans/")) return "post_clean_staging";
  if (u.includes("/maintenance/")) return "cleaning_proof";

  // standards_proof and property_standards are owner-side references, not a
  // cleaner moment. Return null so callers can decide.
  return null;
}

/**
 * Resolve a moment with explicit hint > URL inference > caller default.
 * Use this in UI so labels are consistent without forcing every call site to
 * remember which prefix maps to which moment.
 */
export function resolvePhotoMoment(args: {
  url?: string | null;
  hint?: PhotoMoment | null;
  fallback?: PhotoMoment;
}): PhotoMoment {
  if (args.hint) return args.hint;
  const inferred = inferMomentFromUrl(args.url);
  if (inferred) return inferred;
  return args.fallback ?? "cleaning_proof";
}
